import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  useCompetition,
  useCompetitionFeed,
  useEntryGameweeks,
  useMyEntries,
  type Competition,
  type Entry,
} from "@/api/queries";
import { nextGameweek, useGameweeksFor, useSeasonIndex, type SeasonInfo } from "@/api";
import { placeText, plural, pluralize, untilText } from "@/lib/format";
import { sportMeta } from "@/lib/sports";
import { Alert, SportBadge, SportChip, Spinner, Stat } from "@/ui";
import {
  accessText,
  canCreateTeam,
  roleText,
  useCreateTeam,
  useTourRange,
} from "./competitions/shared";
import "@/ui/teams.css";
import "@/ui/competitions.css";

/** Главная после входа: карточки текущих команд и соревнования, где можно сыграть */
export function TeamsPage() {
  const { user } = useAuth();
  const entries = useMyEntries();
  const { index } = useSeasonIndex();
  const message = (useLocation().state as { message?: string } | null)?.message;
  const active = useMemo(
    () =>
      (entries.data ?? []).filter(
        (e) => e.status !== "finished" && index.get(e.season_id)?.season.status !== "finished",
      ),
    [entries.data, index],
  );
  const seasonIds = useMemo(() => [...new Set(active.map((e) => e.season_id))], [active]);
  const gameweeks = useGameweeksFor(seasonIds);

  const nearestDeadline = useMemo(() => {
    let best: string | null = null;
    for (const id of seasonIds) {
      const gw = nextGameweek(gameweeks.get(id));
      if (gw && (best === null || gw.deadline_at < best)) best = gw.deadline_at;
    }
    return best;
  }, [seasonIds, gameweeks]);

  if (!user) return null;

  const subtitle = [
    "Текущие сезоны",
    pluralize(active.length, "команда", "команды", "команд"),
    nearestDeadline ? `ближайший дедлайн ${untilText(nearestDeadline)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <main className="main">
      {message && <Alert kind="success">{message}</Alert>}

      <header className="page-header">
        <div className="page-header__title">
          <h1>Мои команды</h1>
          <span className="teams-head__sub">{subtitle}</span>
        </div>
      </header>

      {entries.isLoading && <Spinner />}
      {entries.isError && <Alert kind="error">Не удалось загрузить команды</Alert>}
      {entries.data && active.length === 0 && (
        <div className="empty">
          У вас пока нет команд. Выберите соревнование ниже и создайте команду.
        </div>
      )}
      {active.length > 0 && (
        <div className="grid-cards teams-cards">
          {active.map((e) => (
            <TeamCard
              key={e.id}
              entry={e}
              info={index.get(e.season_id)}
              nextGw={nextGameweek(gameweeks.get(e.season_id))}
            />
          ))}
        </div>
      )}

      <CompetitionsBlock entries={entries.data ?? []} index={index} />
    </main>
  );
}

type Filter = "all" | "open" | "mine";

/** Открытые соревнования Picked и мои закрытые: таблица и «Создать команду», где команды нет */
function CompetitionsBlock({
  entries,
  index,
}: {
  entries: Entry[];
  index: Map<string, SeasonInfo>;
}) {
  const feed = useCompetitionFeed();
  const [filter, setFilter] = useState<Filter>("all");
  const createTeam = useCreateTeam();
  const byComp = useMemo(() => new Map(entries.map((e) => [e.competition_id, e])), [entries]);
  const all = useMemo(
    () =>
      (feed.data ?? [])
        .filter((c) => c.status !== "finished")
        // сначала те, где можно собрать команду: это предложение поучаствовать
        .sort((a, b) => Number(canCreateTeam(b)) - Number(canCreateTeam(a))),
    [feed.data],
  );
  const open = all.filter((c) => c.visibility === "public");
  const mine = all.filter((c) => c.visibility !== "public");
  const shown = filter === "open" ? open : filter === "mine" ? mine : all;

  return (
    <section className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: "flex-end" }}>
        <div className="stack" style={{ gap: 4 }}>
          <h2 style={{ fontSize: 20 }}>Соревнования</h2>
          <span className="small muted">
            Открытые от Picked и ваши закрытые. Откройте соревнование, чтобы посмотреть таблицу и
            собрать команду.
          </span>
        </div>
        <div className="chips" role="group" aria-label="Фильтр соревнований">
          {(
            [
              ["all", `Все · ${all.length}`],
              ["open", `Открытые · ${open.length}`],
              ["mine", `Мои закрытые · ${mine.length}`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`chip ${filter === key ? "chip--active" : ""}`}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {createTeam.dialog}
      {feed.isLoading && <Spinner />}
      {feed.isError && <Alert kind="error">Не удалось загрузить соревнования</Alert>}
      {feed.data && shown.length === 0 && (
        <div className="empty">
          {filter === "mine"
            ? "Закрытых соревнований пока нет: вступите по коду или создайте своё."
            : "Сейчас нет открытых соревнований."}
        </div>
      )}
      <div className="stack" style={{ gap: 10 }}>
        {shown.map((c) => (
          <CompetitionRow
            key={c.id}
            comp={c}
            entry={byComp.get(c.id)}
            info={index.get(c.season_id)}
            onCreate={() => createTeam.create(c.id, undefined, c.name)}
            creating={createTeam.pending && createTeam.pendingId === c.id}
          />
        ))}
      </div>

      <div className="comp-cta">
        <span className="small muted">
          Есть код приглашения от друзей или хотите своё соревнование?
        </span>
        <div className="row" style={{ gap: 8 }}>
          <Link to="/competitions#join" className="btn btn--secondary btn--sm">
            Вступить по коду
          </Link>
          <Link to="/competitions/new" className="btn btn--secondary btn--sm">
            Создать соревнование
          </Link>
        </div>
      </div>
    </section>
  );
}

function CompetitionRow({
  comp: c,
  entry,
  info,
  onCreate,
  creating,
}: {
  comp: Competition;
  entry: Entry | undefined;
  info: SeasonInfo | undefined;
  onCreate: () => void;
  creating: boolean;
}) {
  const sport = info?.sport ?? "football";
  const tours = useTourRange(c);
  const canCreate = canCreateTeam(c);
  const meta = [roleText(c), info?.label, tours].filter(Boolean).join(" · ");
  return (
    <article className={`card comp-row ${canCreate ? "comp-row--todo" : ""}`}>
      <SportBadge sport={sport} />
      <div className="comp-row__main">
        <div className="row" style={{ gap: 8 }}>
          <SportChip sport={sport} />
          <span className={`status-chip ${c.visibility === "public" ? "status-chip--owner" : ""}`}>
            {accessText(c)}
          </span>
          {entry && <span className="comp-row__team">✓ {entry.team_name}</span>}
        </div>
        <Link to={`/competitions/${c.id}`} className="comp-row__name">
          {c.name}
        </Link>
        <span className="small muted">{meta}</span>
      </div>
      <div className="comp-row__stat">
        {entry ? (
          <Stat
            label={`место из ${c.entries_count.toLocaleString("ru-RU")}`}
            value={placeText(entry.rank)}
          />
        ) : (
          <Stat
            label={plural(c.entries_count, "команда", "команды", "команд")}
            value={c.entries_count.toLocaleString("ru-RU")}
          />
        )}
      </div>
      <div className="comp-row__actions">
        <Link to={`/competitions/${c.id}`} className="btn btn--secondary btn--sm">
          Таблица
        </Link>
        {entry ? (
          <Link to={`/entries/${entry.id}`} className="btn btn--secondary btn--sm">
            Моя команда
          </Link>
        ) : (
          canCreate && (
            <button type="button" className="btn btn--sm" disabled={creating} onClick={onCreate}>
              Создать команду
            </button>
          )
        )}
      </div>
    </article>
  );
}

/** Карточка команды: очки, место, очки в идущем туре и дедлайн ближайшего */
function TeamCard({
  entry,
  info,
  nextGw,
}: {
  entry: Entry;
  info: SeasonInfo | undefined;
  nextGw: ReturnType<typeof nextGameweek>;
}) {
  const sport = info?.sport ?? "football";
  const meta = sportMeta(sport);
  const gws = useEntryGameweeks(entry.id);
  const competition = useCompetition(entry.competition_id);
  const live = gws.data?.find((g) => g.live);
  const total =
    competition.data?.entries_count ?? gws.data?.find((g) => g.ranked_total)?.ranked_total ?? null;

  return (
    <article
      className="card team-card team-card--sport"
      style={{ "--sport-color": meta.color } as React.CSSProperties}
    >
      <div className="team-card__head">
        <SportChip sport={sport} />
        <span className="small muted">{info?.label ?? ""}</span>
      </div>
      <div>
        <div className="team-card__name">{entry.team_name}</div>
        <div className="muted small">
          {entry.competition_name}
          {total ? ` · ${pluralize(total, "команда", "команды", "команд")}` : ""}
        </div>
      </div>
      <div className="stats" style={{ gap: "12px 20px" }}>
        <Stat label="очков" value={entry.total_points.toLocaleString("ru-RU")} />
        <Stat
          label={total ? `место из ${total.toLocaleString("ru-RU")}` : "место"}
          value={placeText(entry.rank)}
        />
        <div className="team-card__live">
          <Stat
            label="в идущем туре"
            value={live ? live.points.toLocaleString("ru-RU") : <span className="muted">—</span>}
          />
        </div>
      </div>
      {nextGw ? (
        <div className="team-card__deadline">
          <ClockIcon />
          <span>
            Тур {nextGw.number} · дедлайн {untilText(nextGw.deadline_at)}
          </span>
        </div>
      ) : (
        <div className="team-card__deadline team-card__deadline--idle">
          <ClockIcon />
          <span>{live ? `Тур ${live.number} идёт` : "Нет открытых туров"}</span>
        </div>
      )}
      <div className="team-card__actions">
        <Link to={`/entries/${entry.id}`} className="btn">
          Команда
        </Link>
        <Link to={`/entries/${entry.id}/edit`} className="btn btn--secondary">
          Редактировать
        </Link>
      </div>
    </article>
  );
}

function ClockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
